/* eslint-disable react/forbid-prop-types */
import { useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box, Button, Popover, MenuItem, Stack, Typography,
    TextField, alpha, Select, FormControl, InputLabel, Switch,
    FormControlLabel,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import Iconify from 'src/components/Iconify';

/* ──────────────────────────────────────────────────────────────────────────
   SendSettingsStrip
   ────────────────────────────────────────
   Compact horizontal row of "label · Change" affordances rendered
   immediately above the dialog footer. Each opens a small popover with the
   editable control. Settings, not primary actions — visually muted.

   - Audience: One patient / Patient list / Anonymous
   - Schedule: Send now / Send later / Send later & repeat (rrule-lite)
   - Link expiry: numeric days input

   Recurrence payload contract (BE: SendFormsRequestSerializer):
     recurrence = { frequency: 'WEEKLY'|'MONTHLY', interval: 1..12, count: 2..12 }
   When supplied with scheduled_at, the BE creates `count` ConsentRequests
   each scheduled at scheduled_at + i × interval (frequency). ONE_PATIENT
   only — same gating as scheduled_at v1.
   ────────────────────────────────────────────────────────────────────────── */

const AUDIENCE_OPTIONS = [
    { value: 'ONE_PATIENT', label: 'One patient' },
    { value: 'PATIENT_LIST', label: 'Patient list' },
    { value: 'ANONYMOUS', label: 'Anonymous' },
];

const audienceLabel = (v) =>
    AUDIENCE_OPTIONS.find((o) => o.value === v)?.label || 'One patient';

const SettingPill = ({ icon, label, value, ariaLabel, onClick, disabled }) => (
    <Button
        size="small"
        onClick={onClick}
        disabled={disabled}
        aria-label={ariaLabel}
        endIcon={!disabled ? <Iconify icon="eva:edit-2-outline" width={12} /> : null}
        sx={{
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '12px',
            color: '#454F5B',
            borderRadius: '999px',
            px: 1.25,
            py: 0.4,
            minHeight: 26,
            bgcolor: alpha('#919EAB', 0.08),
            border: '1px solid',
            borderColor: alpha('#919EAB', 0.2),
            '&:hover': {
                bgcolor: alpha('#919EAB', 0.14),
                borderColor: alpha('#919EAB', 0.32),
            },
            '&.Mui-disabled': {
                color: '#919EAB',
                bgcolor: alpha('#919EAB', 0.06),
            },
        }}
    >
        <Iconify icon={icon} width={12} style={{ marginRight: 6 }} />
        <Box component="span" sx={{ color: '#637381', mr: 0.5 }}>
            {label}:
        </Box>
        <Box component="span" sx={{ color: '#1C252E', fontWeight: 700 }}>
            {value}
        </Box>
    </Button>
);
SettingPill.propTypes = {
    icon: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    ariaLabel: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
};
SettingPill.defaultProps = { disabled: false };

const SendSettingsStrip = ({
    audienceMode,
    onAudienceModeChange,
    audienceDisabled,
    scheduleMode,
    onScheduleModeChange,
    scheduledAt,
    onScheduledAtChange,
    expiryDays,
    onExpiryDaysChange,
    recurrence,
    onRecurrenceChange,
    recurrenceDisabled,
    recurrenceDisabledReason,
}) => {
    const [audienceAnchor, setAudienceAnchor] = useState(null);
    const [scheduleAnchor, setScheduleAnchor] = useState(null);
    const [expiryAnchor, setExpiryAnchor] = useState(null);

    const hasRecurrence = scheduleMode === 'LATER'
        && recurrence
        && Number(recurrence.count) >= 2;

    const scheduleValue = (() => {
        if (scheduleMode !== 'LATER') return 'Send now';
        if (hasRecurrence) {
            const freq = recurrence.frequency === 'MONTHLY' ? 'monthly' : 'weekly';
            return `Every ${recurrence.interval || 1} ${freq.replace(/ly$/, '')}${(recurrence.interval || 1) === 1 ? '' : 's'} × ${recurrence.count}`;
        }
        return 'Send later';
    })();

    return (
        <Stack
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            aria-label="Send settings"
            sx={{
                alignItems: 'center',
                justifyContent: { xs: 'flex-start', sm: 'flex-end' },
            }}
        >
            <SettingPill
                icon="solar:user-bold-duotone"
                label="Audience"
                value={audienceLabel(audienceMode)}
                ariaLabel="Change audience"
                onClick={(e) => setAudienceAnchor(e.currentTarget)}
                disabled={audienceDisabled}
            />
            <SettingPill
                icon="solar:clock-circle-bold-duotone"
                label="Schedule"
                value={scheduleValue}
                ariaLabel="Change schedule"
                onClick={(e) => setScheduleAnchor(e.currentTarget)}
            />
            <SettingPill
                icon="solar:hourglass-line-duotone"
                label="Link expires"
                value={`${expiryDays} day${expiryDays === 1 ? '' : 's'}`}
                ariaLabel="Change link expiry"
                onClick={(e) => setExpiryAnchor(e.currentTarget)}
            />

            {/* Audience popover */}
            <Popover
                open={Boolean(audienceAnchor)}
                anchorEl={audienceAnchor}
                onClose={() => setAudienceAnchor(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                slotProps={{ paper: { sx: { mt: -0.5, borderRadius: '10px', minWidth: 180, p: 0.5 } } }}
            >
                {AUDIENCE_OPTIONS.map((opt) => (
                    <MenuItem
                        key={opt.value}
                        selected={audienceMode === opt.value}
                        onClick={() => {
                            onAudienceModeChange(opt.value);
                            setAudienceAnchor(null);
                        }}
                        sx={{ fontSize: '13px', borderRadius: '6px' }}
                    >
                        {opt.label}
                    </MenuItem>
                ))}
            </Popover>

            {/* Schedule popover */}
            <Popover
                open={Boolean(scheduleAnchor)}
                anchorEl={scheduleAnchor}
                onClose={() => setScheduleAnchor(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                slotProps={{ paper: { sx: { mt: -0.5, borderRadius: '10px', minWidth: 240, p: 1 } } }}
            >
                <Stack spacing={0.5}>
                    <MenuItem
                        selected={scheduleMode === 'NOW'}
                        onClick={() => { onScheduleModeChange('NOW'); setScheduleAnchor(null); }}
                        sx={{ fontSize: '13px', borderRadius: '6px' }}
                    >
                        Send now
                    </MenuItem>
                    <MenuItem
                        selected={scheduleMode === 'LATER'}
                        onClick={() => onScheduleModeChange('LATER')}
                        sx={{ fontSize: '13px', borderRadius: '6px' }}
                    >
                        Send later
                    </MenuItem>
                    {scheduleMode === 'LATER' && (
                        <Box sx={{ p: 0.5 }}>
                            <Typography sx={{ fontSize: '11px', color: '#637381', mb: 0.5 }}>
                                Send at
                            </Typography>
                            <DateTimePicker
                                value={scheduledAt ? new Date(scheduledAt) : null}
                                onChange={(value) => {
                                    if (value && !Number.isNaN(value.getTime())) {
                                        onScheduledAtChange(value.toISOString());
                                    } else {
                                        onScheduledAtChange('');
                                    }
                                }}
                                minDateTime={new Date()}
                                format="dd/MM/yyyy HH:mm"
                                ampm={false}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        fullWidth: true,
                                        inputProps: { 'aria-label': 'Scheduled send time' },
                                    },
                                }}
                            />
                            {/* Recurrence (rrule-lite). Gated by audience-mode
                                upstream: ONE_PATIENT only, since the BE refuses
                                scheduled sends on PATIENT_LIST. */}
                            <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed', borderColor: 'rgba(145,158,171,0.24)' }}>
                                <FormControlLabel
                                    control={(
                                        <Switch
                                            size="small"
                                            disabled={recurrenceDisabled}
                                            checked={hasRecurrence}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    onRecurrenceChange({
                                                        frequency: recurrence?.frequency || 'WEEKLY',
                                                        interval: recurrence?.interval || 1,
                                                        count: Math.max(2, Number(recurrence?.count) || 4),
                                                    });
                                                } else {
                                                    onRecurrenceChange(null);
                                                }
                                            }}
                                        />
                                    )}
                                    label={(
                                        <Typography sx={{ fontSize: '11.5px', fontWeight: 600 }}>
                                            Repeat
                                        </Typography>
                                    )}
                                    sx={{ ml: 0 }}
                                />
                                {recurrenceDisabled && (
                                    <Typography sx={{ fontSize: '10px', color: 'text.disabled', display: 'block', mt: 0.25 }}>
                                        {recurrenceDisabledReason || 'Recurring sends require one-patient audience.'}
                                    </Typography>
                                )}
                                {hasRecurrence && !recurrenceDisabled && (
                                    <Stack direction="row" spacing={0.75} sx={{ mt: 0.5 }} alignItems="center">
                                        <Typography sx={{ fontSize: '11px', color: '#637381' }}>Every</Typography>
                                        <TextField
                                            size="small"
                                            type="number"
                                            value={recurrence.interval || 1}
                                            onChange={(e) => onRecurrenceChange({
                                                ...recurrence,
                                                interval: Math.max(1, Math.min(12, Number(e.target.value) || 1)),
                                            })}
                                            inputProps={{ min: 1, max: 12, 'aria-label': 'Recurrence interval' }}
                                            sx={{ width: 60 }}
                                        />
                                        <FormControl size="small" sx={{ minWidth: 100 }}>
                                            <InputLabel id="recurrence-frequency" sx={{ fontSize: '11px' }}>
                                                Frequency
                                            </InputLabel>
                                            <Select
                                                labelId="recurrence-frequency"
                                                label="Frequency"
                                                value={recurrence.frequency || 'WEEKLY'}
                                                onChange={(e) => onRecurrenceChange({ ...recurrence, frequency: e.target.value })}
                                                sx={{ fontSize: '11px' }}
                                            >
                                                <MenuItem value="WEEKLY" sx={{ fontSize: '11.5px' }}>week(s)</MenuItem>
                                                <MenuItem value="MONTHLY" sx={{ fontSize: '11.5px' }}>month(s)</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Stack>
                                )}
                                {hasRecurrence && !recurrenceDisabled && (
                                    <Stack direction="row" spacing={0.75} sx={{ mt: 0.75 }} alignItems="center">
                                        <Typography sx={{ fontSize: '11px', color: '#637381' }}>for</Typography>
                                        <TextField
                                            size="small"
                                            type="number"
                                            value={recurrence.count || 4}
                                            onChange={(e) => onRecurrenceChange({
                                                ...recurrence,
                                                count: Math.max(2, Math.min(12, Number(e.target.value) || 2)),
                                            })}
                                            inputProps={{ min: 2, max: 12, 'aria-label': 'Recurrence count' }}
                                            sx={{ width: 60 }}
                                        />
                                        <Typography sx={{ fontSize: '11px', color: '#637381' }}>send(s)</Typography>
                                    </Stack>
                                )}
                            </Box>
                        </Box>
                    )}
                </Stack>
            </Popover>

            {/* Link expiry popover */}
            <Popover
                open={Boolean(expiryAnchor)}
                anchorEl={expiryAnchor}
                onClose={() => setExpiryAnchor(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                slotProps={{ paper: { sx: { mt: -0.5, borderRadius: '10px', minWidth: 200, p: 1.25 } } }}
            >
                <Typography sx={{ fontSize: '11px', color: '#637381', mb: 0.5 }}>
                    Link expires after
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                        size="small"
                        type="number"
                        value={expiryDays}
                        onChange={(e) => onExpiryDaysChange(Math.max(1, Number(e.target.value) || 1))}
                        inputProps={{
                            min: 1,
                            max: 90,
                            'aria-label': 'Link expiry days',
                        }}
                        sx={{ width: 96 }}
                    />
                    <Typography sx={{ fontSize: '12px', color: '#637381' }}>
                        days
                    </Typography>
                </Stack>
            </Popover>
        </Stack>
    );
};

SendSettingsStrip.propTypes = {
    audienceMode: PropTypes.oneOf(['ONE_PATIENT', 'PATIENT_LIST', 'ANONYMOUS']).isRequired,
    onAudienceModeChange: PropTypes.func.isRequired,
    audienceDisabled: PropTypes.bool,
    scheduleMode: PropTypes.oneOf(['NOW', 'LATER']).isRequired,
    onScheduleModeChange: PropTypes.func.isRequired,
    scheduledAt: PropTypes.string,
    onScheduledAtChange: PropTypes.func,
    expiryDays: PropTypes.number.isRequired,
    onExpiryDaysChange: PropTypes.func.isRequired,
    recurrence: PropTypes.shape({
        frequency: PropTypes.oneOf(['WEEKLY', 'MONTHLY']),
        interval: PropTypes.number,
        count: PropTypes.number,
    }),
    onRecurrenceChange: PropTypes.func,
    recurrenceDisabled: PropTypes.bool,
    recurrenceDisabledReason: PropTypes.string,
};

SendSettingsStrip.defaultProps = {
    audienceDisabled: false,
    scheduledAt: '',
    onScheduledAtChange: () => {},
    recurrence: null,
    onRecurrenceChange: () => {},
    recurrenceDisabled: false,
    recurrenceDisabledReason: '',
};

export default SendSettingsStrip;
